import { getSourceFromUserAgent } from "~/utils/device.utils";
import { DeviceTypes } from "~/types/MFA.types";

export const OF_PENDING_CONNECTION_KEY = "ofPendingConnection";
export const OF_CALLBACK_PATH = "/openFinance/callback";
export const OF_CUSTOM_SCHEME_CALLBACK = "moneytor://openFinance/callback";
export const OF_SKIP_BROWSER_FINISHED_KEY = "ofSkipBrowserFinishedRetrieve";

/**
 * iOS leaves the in-app browser when the bank app (for example Leumi) takes
 * over. The bank returns through the system browser, which only comes back to
 * Moneytor from our https callback. Android must stay in the in-app iframe.
 * Chrome Custom Tabs plus verified App Links for the whole site host capture
 * the https redirect immediately, so the app skips the bank picker and starts
 * importing.
 */
export function shouldUseOpenFinanceRedirectJourney(options: {
  userAgent?: string | null;
  requested?: boolean;
}): boolean {
  const source = getSourceFromUserAgent(options.userAgent);
  if (source === DeviceTypes.ANDROID) return false;
  if (options.requested) return true;
  return source === DeviceTypes.IOS;
}

export function shouldOpenOpenFinanceExternally(
  platform: "ios" | "android" | "web"
): boolean {
  return platform === "ios";
}

export type PendingOpenFinanceConnection = {
  connectionId: string;
  restrictedTo?: "CARD";
};

export function buildOpenFinanceRedirectUrl(origin: string): string {
  const url = new URL(OF_CALLBACK_PATH, `${origin.replace(/\/$/, "")}/`);
  url.searchParams.set("of_return", "1");
  return url.toString();
}

/**
 * Custom-scheme target once the user is leaving the browser. Open Finance
 * itself must be given the https callback: a moneytor:// redirect is ignored
 * and the journey falls through to the organization website.
 */
export function buildOpenFinanceAppRedirectUrl(): string {
  const url = new URL(OF_CUSTOM_SCHEME_CALLBACK);
  url.searchParams.set("of_return", "1");
  return url.toString();
}

const IOS_APP_UA_MARKER = "Version/8.0.2 Safari/600.2.5";

/**
 * Chrome (and other iOS browsers) block custom-scheme redirects, so they get
 * the tap-to-return page instead. Mobile Safari can follow a document redirect.
 * The iOS app webview must keep loading the page so it can finish the import.
 */
export function openFinanceMobileBrowserReturnUrl(
  requestUrl: string,
  userAgent: string | null | undefined
): string | null {
  let url: URL;
  try {
    url = new URL(requestUrl);
  } catch {
    return null;
  }
  if (!isOpenFinanceHandoffLocation(url.pathname, url.search)) return null;

  const ua = userAgent || "";
  if (ua.includes(IOS_APP_UA_MARKER) || ua.includes("_moneytor")) return null;
  if (/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua)) return null;
  if (!/iPhone|iPad|iPod/i.test(ua)) return null;

  const target = new URL(OF_CUSTOM_SCHEME_CALLBACK);
  url.searchParams.forEach((value, key) => {
    target.searchParams.append(key, value);
  });
  return target.toString();
}

export function appendOpenFinanceRedirectUrl(
  connectUrl: string,
  redirectUrl: string
): string {
  try {
    const url = new URL(connectUrl);
    url.searchParams.set("redirectUrl", redirectUrl);
    return url.toString();
  } catch {
    return connectUrl;
  }
}

export function storePendingOpenFinanceConnection(
  pending: PendingOpenFinanceConnection
) {
  try {
    localStorage.setItem(OF_PENDING_CONNECTION_KEY, JSON.stringify(pending));
  } catch {
    // ignore quota / private mode
  }
}

export function readPendingOpenFinanceConnection(): PendingOpenFinanceConnection | null {
  try {
    const raw = localStorage.getItem(OF_PENDING_CONNECTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingOpenFinanceConnection;
    if (!parsed?.connectionId || typeof parsed.connectionId !== "string") {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingOpenFinanceConnection() {
  try {
    localStorage.removeItem(OF_PENDING_CONNECTION_KEY);
  } catch {
    // ignore
  }
}

export function isOpenFinanceCallbackPath(path: string | null | undefined): boolean {
  if (!path) return false;
  const pathname = path.split("?")[0]?.split("#")[0] ?? path;
  return pathname === OF_CALLBACK_PATH || pathname === `${OF_CALLBACK_PATH}/`;
}

export function isOpenFinanceHandoffLocation(
  pathname: string,
  search: string = ""
): boolean {
  if (isOpenFinanceCallbackPath(pathname)) return true;
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search
  );
  return params.get("of_return") === "1";
}

const inflightRetrieves = new Set<string>();

export function beginOpenFinanceRetrieve(connectionId: string): boolean {
  if (!connectionId || inflightRetrieves.has(connectionId)) return false;
  inflightRetrieves.add(connectionId);
  return true;
}

export function endOpenFinanceRetrieve(connectionId: string) {
  inflightRetrieves.delete(connectionId);
}
